import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Mail, Phone, UserRound } from 'lucide-react';
import type { PublicOfficial } from '../lib/api';
import { competitionPath, pilotProfilePath } from '../lib/api';
import type { PublicOrganiser } from '../lib/types';

function Avatar({
  name,
  imageUrl,
  isLogo = false,
}: {
  name: string;
  imageUrl: string | null;
  /** Organization logos use contain-fit instead of crop. */
  isLogo?: boolean;
}) {
  return (
    <div className="mx-auto flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted ring-2 ring-sky-500/20 sm:h-32 sm:w-32">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className={
            isLogo
              ? 'h-[85%] w-[85%] object-contain p-1'
              : 'h-full w-full object-cover object-top'
          }
        />
      ) : isLogo ? (
        <Building2 className="h-12 w-12 text-muted-foreground/50" strokeWidth={1.25} />
      ) : (
        <UserRound className="h-12 w-12 text-muted-foreground/50" strokeWidth={1.25} />
      )}
    </div>
  );
}

interface PersonCardProps {
  name: string;
  role: string;
  imageUrl: string | null;
  isLogo?: boolean;
  email?: string | null;
  phone?: string | null;
  showContact?: boolean;
  index?: number;
  href?: string;
}

function PersonCard({
  name,
  role,
  imageUrl,
  isLogo = false,
  email,
  phone,
  showContact = false,
  index = 0,
  href,
}: PersonCardProps) {
  const hasContact = showContact && (email || phone);
  const className =
    'flex h-full flex-col items-center rounded-2xl border border-border bg-card px-5 py-8 text-center backdrop-blur-sm transition-colors hover:border-sky-500/25 hover:bg-muted';
  const identity = (
    <>
      <Avatar name={name} imageUrl={imageUrl} isLogo={isLogo} />
      <h3 className="mt-5 text-base font-semibold leading-snug text-sky-700 dark:text-sky-300 sm:text-lg">
        {name}
      </h3>
      <p className="mt-1.5 text-sm text-muted-foreground">{role}</p>
    </>
  );
  const contact = hasContact ? (
        <div className="mt-4 w-full space-y-2.5 border-t border-border pt-4 text-sm text-muted-foreground">
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center justify-center gap-2 transition-colors hover:text-sky-800 dark:hover:text-sky-300"
            >
              <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{email}</span>
            </a>
          )}
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex items-center justify-center gap-2 transition-colors hover:text-sky-800 dark:hover:text-sky-300"
            >
              <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span>{phone}</span>
            </a>
          )}
        </div>
      ) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.35) }}
      className="h-full"
    >
      <article className={className}>
        {href ? (
          <Link to={href} className="flex flex-col items-center">
            {identity}
          </Link>
        ) : (
          identity
        )}
        {contact}
      </article>
    </motion.div>
  );
}

interface OfficialCardProps {
  official: PublicOfficial;
  showContact?: boolean;
  index?: number;
  href?: string;
}

export function OfficialCard({ official, showContact = false, index = 0, href }: OfficialCardProps) {
  return (
    <PersonCard
      name={official.name}
      role={official.role}
      imageUrl={official.imageUrl}
      email={official.email}
      phone={official.phone}
      showContact={showContact}
      index={index}
      href={href}
    />
  );
}

interface OrganizerCardProps {
  organiser: PublicOrganiser;
  index?: number;
}

export function OrganizerCard({ organiser, index = 0 }: OrganizerCardProps) {
  return (
    <PersonCard
      name={organiser.name}
      role={organiser.role}
      imageUrl={organiser.logoUrl}
      isLogo
      index={index}
    />
  );
}

interface OrganizingTeamSectionProps {
  competitionId: string;
  officials: PublicOfficial[];
  organiser?: PublicOrganiser | null;
  /** Max cards on home preview; omit for full list */
  limit?: number;
  showContact?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  /** When false, hide the top bar (full page provides its own heading) */
  showHeader?: boolean;
}

export function OrganizingTeamSection({
  competitionId,
  officials,
  organiser,
  limit,
  showContact = false,
  isLoading,
  error,
  showHeader = true,
}: OrganizingTeamSectionProps) {
  if (isLoading) {
    return (
      <section className="mx-auto max-w-7xl px-6 py-12">
        <p className="text-muted-foreground">Loading organizing team…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mx-auto max-w-7xl px-6 py-12">
        <p className="text-red-400">{error.message || 'Could not load team'}</p>
      </section>
    );
  }

  const hasOrganiser = Boolean(organiser?.name);
  if (officials.length === 0 && !hasOrganiser) {
    if (!showHeader) {
      return (
        <div className="rounded-2xl border border-dashed border-border px-6 py-16 text-center">
          <p className="text-lg text-muted-foreground">No organizing team listed yet</p>
        </div>
      );
    }
    return null;
  }

  // Home: show organiser first, then officials up to limit total cards
  const officialSlots =
    limit != null
      ? Math.max(0, limit - (hasOrganiser ? 1 : 0))
      : officials.length;
  const officialItems =
    limit != null ? officials.slice(0, officialSlots) : officials;

  return (
    <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
      {showHeader && (
        <div className="mb-8 flex items-baseline justify-between gap-4 border-b border-border pb-3">
          <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
            Organizing Team
          </h2>
          {limit != null && (
            <Link
              to={`${competitionPath(competitionId, 'event')}#officials`}
              className="shrink-0 text-sm font-medium text-sky-700 dark:text-sky-400 underline-offset-4 hover:text-sky-800 dark:hover:text-sky-300 hover:underline"
            >
              Show all
            </Link>
          )}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {hasOrganiser && organiser ? (
          <OrganizerCard organiser={organiser} index={0} />
        ) : null}
        {officialItems.map((official, index) => (
          <OfficialCard
            key={official.id}
            official={official}
            showContact={showContact}
            index={index + (hasOrganiser ? 1 : 0)}
            href={
              pilotProfilePath(competitionId, { aeroJudgeId: official.aeroJudgeId }) ?? undefined
            }
          />
        ))}
      </div>
    </section>
  );
}
